import { useCallback } from 'react'
import nacl from 'tweetnacl'
import { encodeBase64, decodeBase64, encodeUTF8, decodeUTF8 } from 'tweetnacl-util'
import { api } from '../services/api'
import {
  DoubleRatchet,
  wireEnc,
  wireDec,
  createFallbackDRState,
  hmac256,
  kdfRoot,
  genKP,
  x3dhInitiate,
  type KeyPair,
  type SessionState,
  type DRState,
  type PreKeyBundle as CryptoPreKeyBundle,
} from '../crypto/signal-protocol'
import { getSignalStore } from '../crypto/signal-store'

/**
 * Signal Client Hook
 * A generic version for arbitrary recipients.
 * Used for Group Chat E2EE to establish sessions with multiple members.
 * Now uses Double Ratchet (v5) with legacy v4 fallback.
 */

// Storage keys (shared with useSignalProtocol)
const STORAGE_KEYS = {
  IDENTITY_KEY: 'lynkstar_identity_key',
  SESSIONS: 'lynkstar_sessions_v4',
  MY_UUID: 'lynkstar_my_uuid',
}

const isUUID = (s: string): boolean =>
  /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(s)

const getRecipientUUID = (id: string): string => {
  if (isUUID(id)) return id
  const s = localStorage.getItem(`lynkstar_uuid_${id}`)
  return (s && isUUID(s)) ? s : id
}

let _uuid: string | null = null
const getMyUUID = (): string | null => {
  const s = localStorage.getItem(STORAGE_KEYS.MY_UUID)
  if (s && isUUID(s)) { _uuid = s; return s }
  if (_uuid && isUUID(_uuid)) return _uuid
  try {
    const u = JSON.parse(localStorage.getItem('lynkstar_user') || '{}')
    if (u.id && isUUID(u.id)) { _uuid = u.id; localStorage.setItem(STORAGE_KEYS.MY_UUID, u.id); return u.id }
  } catch (e) { console.warn('[E2EE] suppressed:', e) }
  return null
}

// Legacy session type (v4 compat)
interface LegSess {
  recipientId: string; sharedKey: Uint8Array
  sendCounter: number; receiveCounter: number
}

function loadLeg(tid: string): LegSess | null {
  try {
    const v4 = localStorage.getItem('lynkstar_sessions_v4')
    if (!v4) return null
    const p = JSON.parse(v4); const s = p[tid]; if (!s) return null
    return { recipientId: tid, sharedKey: decodeBase64(s.sharedKey), sendCounter: s.sendCounter || 0, receiveCounter: s.receiveCounter || 0 }
  } catch (e) { console.warn('[E2EE] suppressed:', e); return null }
}

function saveLeg(s: LegSess): void {
  try {
    const all = JSON.parse(localStorage.getItem('lynkstar_sessions_v4') || '{}')
    all[s.recipientId] = { ...s, sharedKey: encodeBase64(s.sharedKey) }
    localStorage.setItem('lynkstar_sessions_v4', JSON.stringify(all))
  } catch (e) { console.warn('[E2EE] suppressed:', e) }
}

async function fetchTimeout<T>(p: Promise<T>, ms = 5000): Promise<T> {
  return Promise.race([p, new Promise<T>((_, rej) => setTimeout(() => rej(new Error('timeout')), ms))])
}

export function useSignalClient() {
  const getStoredIdentityKey = useCallback((): KeyPair | null => {
    try {
      const raw = localStorage.getItem(STORAGE_KEYS.IDENTITY_KEY)
      if (!raw) return null
      const p = JSON.parse(raw)
      return { publicKey: decodeBase64(p.publicKey), secretKey: decodeBase64(p.secretKey) }
    } catch (e) { console.warn('[E2EE] suppressed:', e); return null }
  }, [])

  // Establish v5 Double Ratchet session
  const establishSession = useCallback(async (targetId: string, currentUserId?: string): Promise<SessionState> => {
    const store = await getSignalStore()

    // Check existing v5
    const existing = await store.loadSession(targetId)
    if (existing) return existing

    const myIK = await store.loadIdentityKeyPair() || getStoredIdentityKey()
    if (!myIK) throw new Error('Identity key not initialized')

    console.log('[SignalClient v5] Establishing session with:', targetId)

    let theirIK: Uint8Array | null = null
    let bundleData: CryptoPreKeyBundle | null = null

    // Try PreKeyBundle
    try {
      const raw = await fetchTimeout(api.getPreKeyBundle(targetId), 3000)
      const ikStr = raw.identityKey || (raw as unknown as Record<string, string>).identity_key
      if (ikStr) {
        theirIK = decodeBase64(ikStr as string)
        const spkStr = raw.signedPreKey || (raw as unknown as Record<string, string>).signed_pre_key
        if (spkStr) {
          bundleData = {
            identityKey: theirIK,
            signedPreKey: decodeBase64(spkStr as string),
            signedPreKeyId: raw.signedPreKeyId || 1,
            signedPreKeySignature: decodeBase64((raw.signedPreKeySignature || '') as string),
            oneTimePreKey: raw.oneTimePreKey ? decodeBase64(raw.oneTimePreKey as string) : undefined,
            oneTimePreKeyId: raw.oneTimePreKeyId as number | undefined,
          }
        }
      }
    } catch (e) { console.warn('[E2EE] suppressed:', e) }

    // Fallback: user info
    if (!theirIK) {
      try {
        const u = await fetchTimeout(api.getUserByUsername(targetId), 3000)
        if (u.publicKey) theirIK = decodeBase64(u.publicKey)
      } catch (e) { console.warn('[E2EE] suppressed:', e) }
    }

    let drState: DRState

    if (theirIK && bundleData) {
      // Full X3DH
      const { sk } = x3dhInitiate(myIK, bundleData)
      drState = DoubleRatchet.alice(sk, bundleData.signedPreKey).s
    } else if (theirIK) {
      // DH-based
      const dhResult = nacl.scalarMult(myIK.secretKey, theirIK)
      const myPub = encodeBase64(myIK.publicKey)
      const theirPub = encodeBase64(theirIK)
      const sorted = [myPub, theirPub].sort()
      const ctx = decodeUTF8('lynkstar:e2ee:' + sorted[0] + ':' + sorted[1])
      const combined = new Uint8Array(dhResult.length + ctx.length)
      combined.set(dhResult, 0); combined.set(ctx, dhResult.length)
      const sk = nacl.hash(combined).slice(0, 32)
      const kp = genKP()
      const isFirst = sorted[0] === myPub
      const simDH = hmac256(sk, new TextEncoder().encode('DH_SIM'))
      const { rk, ck } = kdfRoot(sk, simDH)
      drState = {
        dhSend: kp, dhRemote: null, rk,
        sCK: isFirst ? ck : sk.slice(0, 32),
        rCK: isFirst ? sk.slice(0, 32) : ck,
        sN: 0, rN: 0, pN: 0, mk: {},
      }

      // Also create legacy session
      const legKey = nacl.hash(combined).slice(0, 32)
      saveLeg({ recipientId: targetId, sharedKey: legKey, sendCounter: 0, receiveCounter: 0 })
    } else {
      // Symmetric fallback
      const myId = getMyUUID() || currentUserId || 'unknown'
      const theirId = getRecipientUUID(targetId)
      const sorted = [myId, theirId].sort()
      drState = createFallbackDRState(myId, theirId)

      // Legacy compat
      const keyMaterial = 'lynkstar:v4:' + sorted[0] + ':' + sorted[1]
      const localKey = nacl.hash(decodeUTF8(keyMaterial)).slice(0, 32)
      saveLeg({ recipientId: targetId, sharedKey: localKey, sendCounter: 0, receiveCounter: 0 })
    }

    const sess: SessionState = {
      rid: targetId, dr: drState, x3dh: !!bundleData,
      ts: Date.now(), lu: Date.now(),
    }
    await store.saveSession(targetId, sess)
    return sess
  }, [getStoredIdentityKey])

  // Encrypt to user (tries v5 Double Ratchet, falls back to legacy)
  const encryptToUser = useCallback(async (targetId: string, plaintext: string, currentUserId?: string): Promise<string> => {
    const store = await getSignalStore()

    // Try v5
    let sess = await store.loadSession(targetId)
    if (!sess) {
      try { sess = await establishSession(targetId, currentUserId) } catch (e) { console.warn('[E2EE] suppressed:', e) }
    }

    if (sess) {
      const ratchet = new DoubleRatchet(sess.dr)
      try {
        const { hdr, ct, nonce } = ratchet.encrypt(decodeUTF8(plaintext))
        sess.dr = ratchet.s; sess.lu = Date.now()
        await store.saveSession(targetId, sess)
        return encodeBase64(wireEnc(hdr, nonce, ct))
      } catch (e) { console.warn('[E2EE] DR decrypt failed, falling back to legacy:', e) }
    }

    // Legacy fallback
    let leg = loadLeg(targetId)
    if (!leg) {
      await establishSession(targetId, currentUserId)
      leg = loadLeg(targetId)
    }
    if (!leg) throw new Error('Cannot encrypt: no session')

    const nonce = nacl.randomBytes(24)
    const ct = nacl.secretbox(decodeUTF8(plaintext), nonce, leg.sharedKey)
    const combined = new Uint8Array(nonce.length + ct.length)
    combined.set(nonce, 0); combined.set(ct, nonce.length)
    leg.sendCounter++
    saveLeg(leg)
    return encodeBase64(combined)
  }, [establishSession])

  // Decrypt from user
  const decryptFromUser = useCallback(async (senderId: string, encryptedData: string, currentUserId?: string): Promise<string> => {
    if (encryptedData.startsWith('PLAIN:')) {
      try { return encodeUTF8(decodeBase64(encryptedData.slice(6))) } catch (e) { console.warn('[E2EE] base64 decode failed:', e); return encryptedData }
    }

    const store = await getSignalStore()
    const data = decodeBase64(encryptedData)

    // Try v5 Double Ratchet
    const wireMsg = wireDec(data)
    if (wireMsg) {
      let sess = await store.loadSession(senderId)
      if (!sess) {
        try { sess = await establishSession(senderId, currentUserId) } catch (e) { console.warn('[E2EE] suppressed:', e) }
      }
      if (sess) {
        const ratchet = new DoubleRatchet(sess.dr)
        const pt = ratchet.decrypt(wireMsg.hdr, wireMsg.ct, wireMsg.nonce)
        if (pt) {
          sess.dr = ratchet.s; sess.lu = Date.now()
          await store.saveSession(senderId, sess)
          return encodeUTF8(pt)
        }
      }
    }

    // Legacy fallback
    let leg = loadLeg(senderId)
    if (!leg) {
      try { await establishSession(senderId, currentUserId); leg = loadLeg(senderId) } catch (e) { console.warn('[E2EE] suppressed:', e) }
    }
    if (!leg) return '[Encrypted Message - Session Failed]'

    try {
      const nonce = data.slice(0, 24)
      const ct = data.slice(24)
      const dec = nacl.secretbox.open(ct, nonce, leg.sharedKey)
      if (!dec) return '[Decryption Failed]'
      return encodeUTF8(dec)
    } catch (e) { console.warn('[E2EE] decrypt error:', e); return '[Decryption Error]' }
  }, [establishSession])

  return { establishSession, encryptToUser, decryptFromUser }
}
